import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Globe, Loader2, Sun, Snowflake, RefreshCw, Users } from 'lucide-react';
import { toast } from 'sonner';
import { TIMEZONE_OPTION_GROUPS } from '../utils/timezoneOptions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_OPTIONS = [
  { value: 'auto', label: 'Changes clocks (normal DST rules)', hint: 'Spring forward / fall back as the region always has.' },
  { value: 'standard', label: 'No DST — stay on standard (winter) time all year', hint: 'Alberta / Saskatchewan style: MST or CST year-round.' },
  { value: 'daylight', label: 'No DST — stay on summer (daylight) time all year', hint: 'British Columbia style: PDT year-round.' },
];

const fmtFrom = (iso) => { if (!iso) return ''; const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); };
const regionLabel = (value) => {
  for (const g of TIMEZONE_OPTION_GROUPS) {
    const o = g.options.find((x) => x.value === value);
    if (o) return o.label;
  }
  return value;
};

export default function DaylightSavingCard({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(null);
  const [region, setRegion] = useState('');
  const [mode, setMode] = useState('auto');
  const [from, setFrom] = useState('');
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/company/timezone-settings`, { headers });
      setCurrent(data);
      setRegion(data.timezone_region || 'America/Toronto');
      setMode(data.dst_mode || 'auto');
      setFrom(data.dst_from || '');
    } catch (e) {
      toast.error('Could not load time zone settings');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!region) return;
    let alive = true;
    axios.get(`${API}/timezone/preview`, { headers, params: { region, dst_mode: mode, dst_from: from || undefined } })
      .then(({ data }) => { if (alive) setPreview(data); })
      .catch(() => { if (alive) setPreview(null); });
    return () => { alive = false; };
  }, [region, mode, from, token]);

  const dirty = current && (region !== (current.timezone_region || '') || mode !== (current.dst_mode || 'auto') || (from || '') !== (current.dst_from || ''));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/company/timezone-settings`,
        { timezone_region: region, dst_mode: mode, dst_from: mode === 'auto' ? null : (from || null) }, { headers });
      toast.success(`Saved — company clock is now ${data.current_abbreviation} (${data.current_offset}). ${data.workers_updated} worker${data.workers_updated === 1 ? '' : 's'} updated.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card className="shadow-lg mb-6"><CardContent className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></CardContent></Card>;

  return (
    <Card className="shadow-lg mb-6 border-2 border-[#2D3748]" data-testid="dst-settings-card">
      <CardHeader>
        <CardTitle className="text-lg font-bold flex items-center gap-2"><Globe className="w-5 h-5 text-[#1E88E5]" /> Time Zone &amp; Daylight Saving</CardTitle>
        <p className="text-sm text-slate-400 mt-1">
          Some provinces and states have stopped changing their clocks (e.g. Alberta, British Columbia, Saskatchewan). If your workers'
          times show an hour off in spring/summer, turn daylight saving off here. Workers in a different province can be set individually
          from <span className="text-slate-200">People → Manage Users → Edit → Daylight saving time</span>.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="rounded-lg bg-[#0F1923] border border-[#2D3748] p-3 flex flex-wrap items-center gap-4" data-testid="dst-current">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Company clock right now</div>
            <div className="text-xl font-semibold text-white" data-testid="dst-current-time">{current?.current_local_time} <span className="text-base text-[#00C896]">{current?.current_abbreviation}</span> <span className="text-sm text-slate-400">({current?.current_offset})</span></div>
          </div>
          <div className="text-sm text-slate-300">
            {regionLabel(current?.timezone_region)} · {current?.dst_mode === 'auto' ? 'changes clocks with the region' : `no DST (${current?.current_abbreviation} all year${current?.dst_from ? ` from ${fmtFrom(current.dst_from)}` : ''})`}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Company time zone</Label>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger data-testid="dst-region-select"><SelectValue placeholder="Pick a region" /></SelectTrigger>
              <SelectContent>
                {TIMEZONE_OPTION_GROUPS.map((group) => (
                  <React.Fragment key={group.label}>
                    <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">{group.label}</div>
                    {group.options.map((opt) => <SelectItem key={opt.value} value={opt.value} data-testid={`dst-region-option-${opt.value}`}>{opt.label}</SelectItem>)}
                  </React.Fragment>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">Default for new workers and for workers without their own time zone.</p>
          </div>
          <div className="space-y-2">
            <Label>Daylight saving time</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="dst-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`dst-mode-option-${o.value}`}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">{MODE_OPTIONS.find((o) => o.value === mode)?.hint}</p>
            {preview && !preview.region_observes_dst && mode !== 'auto' && (
              <p className="text-xs text-amber-300" data-testid="dst-no-effect-note">This region never changes clocks, so this setting has no effect.</p>
            )}
            {preview && preview.region_observes_dst && mode !== 'auto' && preview.dst_mode === 'auto' && (
              <p className="text-xs text-amber-300" data-testid="dst-unsupported-note">Turning DST off isn't available for half-hour zones (e.g. Newfoundland); the region's normal rules will be used.</p>
            )}
          </div>
        </div>

        {mode !== 'auto' && (
          <div className="space-y-2 max-w-sm" data-testid="dst-from-block">
            <Label>Applies from (optional)</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="dst-from-input" />
            <p className="text-xs text-slate-500">
              The date your region stopped changing clocks. Punches <b>before</b> it keep the old spring-forward / fall-back rules, so
              last year's payroll reports don't shift by an hour. Leave blank to apply to all dates. You can come back and change this later.
            </p>
          </div>
        )}

        {preview && (
          <div className="rounded-lg border border-[#2D3748] p-3 text-sm" data-testid="dst-preview">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">What a 5:00 PM UTC punch would read with these settings</div>
            <div className="flex flex-wrap gap-6">
              {preview.samples?.map((smp) => (
                <div key={smp.month} className="flex items-center gap-2" data-testid={`dst-preview-${smp.month.toLowerCase()}`}>
                  {smp.month === 'January' ? <Snowflake className="w-4 h-4 text-sky-300" /> : <Sun className="w-4 h-4 text-amber-300" />}
                  <span className="text-slate-400">{smp.month}:</span> <span className="text-white font-medium">{smp.local}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={saving || !dirty} className="bg-[#1E88E5] hover:bg-blue-600" data-testid="dst-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />} Save &amp; apply to workers
          </Button>
          {!dirty && <span className="text-xs text-slate-500">No changes</span>}
        </div>

        <div className="pt-2 border-t border-[#2D3748]">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-200"><Users className="w-4 h-4" /> Workers with their own daylight-saving setting
            <span className="text-xs text-slate-500" data-testid="dst-override-count">({current?.worker_override_count || 0})</span></div>
          {current?.worker_overrides?.length ? (
            <ul className="mt-2 space-y-1 text-sm" data-testid="dst-override-list">
              {current.worker_overrides.map((w) => (
                <li key={w.id} className="flex flex-wrap justify-between gap-2 rounded bg-[#0F1923] px-3 py-1.5" data-testid={`dst-override-${w.id}`}>
                  <span className="text-white">{w.name}</span>
                  <span className="text-slate-400">{regionLabel(w.timezone_region)} · {w.dst_mode === 'auto' ? 'changes clocks' : `no DST (${w.current_abbreviation} all year${w.dst_from ? ` from ${fmtFrom(w.dst_from)}` : ''})`}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-500 mt-1">None — every worker follows the company setting above.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
